import {
  Caption1,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Diamond12Filled,
  Flag16Filled,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useState, type DragEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  addMonthsDay,
  fromDay,
  isISODate,
  toDay,
  todayISO,
  type DayNumber,
  type ISODate,
} from "@/engine/date";
import { usePlanSurface } from "@/features/access/use-plan-access";
import type { ExecutionTask } from "@/engine/types";
import { CalendarDayCell } from "@/features/calendar-view/CalendarDayCell";
import { CalendarHeader } from "@/features/calendar-view/CalendarHeader";
import { EventSegment } from "../features/calendar-view/EventSegment";
import { SchedulePeekDrawer } from "@/features/calendar-view/SchedulePeekDrawer";
import { LinkedTasksDialog } from "@/features/tasks/LinkedTasksDialog";
import {
  buildEvents,
  CALENDAR_DAY_MIN_WIDTH,
  CALENDAR_GRID_MIN_WIDTH,
  DEFAULT_LAYERS,
  eventsOnDay,
  layoutWeek,
  monthGrid,
  moveExecutionDates,
  unscheduledTasks,
  type CalendarEvent,
  type CalendarLayer,
} from "@/features/calendar-view/calendar-layout";
import { CalendarSidebar, LAYER_COLORS } from "@/features/calendar-view/CalendarSidebar";
import { useTaskContext } from "@/features/tasks/task-ui";
import { formatDate, weekdayNames } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { knownAssignees } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";

const DND_TYPE = "application/x-wavex-execution-task";
const WEEKDAYS = weekdayNames("short");

const LANE_H = 20;
const LANE_GAP = 2;
const TOP_OFFSET = 28;

const useStyles = makeStyles({
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: "hidden",
  },
  toolbar: {
    flexShrink: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  body: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  weekDaysHeader: {
    display: "grid",
    gridTemplateColumns: `repeat(7, minmax(${CALENDAR_DAY_MIN_WIDTH}px, 1fr))`,
    borderBottom: "1px solid var(--cal-line)",
    backgroundColor: tokens.colorNeutralBackground1,
    position: "sticky",
    top: 0,
    zIndex: 3,
    flexShrink: 0,
  },
  weekDayCell: {
    padding: "8px 0",
    textAlign: "center",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    borderRight: "1px solid var(--cal-line)",
    minWidth: `${CALENDAR_DAY_MIN_WIDTH}px`,
    overflow: "hidden",
    ":last-child": {
      borderRight: "none",
    },
  },
  gridWrap: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: "auto",
  },
  gridInner: {
    display: "flex",
    flexDirection: "column",
    minWidth: `${CALENDAR_GRID_MIN_WIDTH}px`,
    width: "100%",
    minHeight: "100%",
    boxSizing: "border-box",
  },
  gridBody: {
    display: "flex",
    flexDirection: "column",
    flex: "1 0 auto",
    minHeight: 0,
  },
  weekRow: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flex: "1 0 auto",
    minWidth: `${CALENDAR_GRID_MIN_WIDTH}px`,
    borderBottom: "1px solid var(--cal-line)",
  },
  weekGrid: {
    display: "grid",
    gridTemplateColumns: `repeat(7, minmax(${CALENDAR_DAY_MIN_WIDTH}px, 1fr))`,
    width: "100%",
    minWidth: `${CALENDAR_GRID_MIN_WIDTH}px`,
    flex: "1 0 auto",
    minHeight: "inherit",
  },
  lanes: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 1,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  },
  colorDot: {
    width: "8px",
    height: "8px",
    borderRadius: "2px",
  },
});

export function ProjectCalendarPage() {
  const styles = useStyles();
  const canPlace = usePlanSurface().editSchedule;
  const ctx = useTaskContext();
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const calendar = useProjectStore((s) => s.derived.calendar);
  const me = useCatalogStore((s) => s.profile.displayName);
  const s = useProjectStore.getState;

  const [searchParams] = useSearchParams();
  const dateQuery = searchParams.get("date");
  const [anchor, setAnchor] = useState<ISODate>(() => (dateQuery && isISODate(dateQuery) ? dateQuery : todayISO()));
  useEffect(() => {
    if (dateQuery && isISODate(dateQuery)) setAnchor(dateQuery);
  }, [dateQuery]);
  const [layers, setLayers] = useState<CalendarLayer[]>(DEFAULT_LAYERS);
  const [assignee, setAssignee] = useState<string>("");
  const [hideDone, setHideDone] = useState(false);

  const [drag, setDrag] = useState<string | null>(null);
  const [overDay, setOverDay] = useState<DayNumber | null>(null);
  const [peek, setPeek] = useState<{ id: string; kind: CalendarEvent["kind"] } | null>(null);
  const [linkedScheduleId, setLinkedScheduleId] = useState<string | null>(null);

  const today = ctx.today;
  const assignees = useMemo(() => knownAssignees(project), [project]);

  // Dropdown options
  const assigneesList = useMemo(() => [
    { value: "", label: "모든 담당" },
    { value: me, label: `${me} (나)` },
    ...assignees.filter((a) => a !== me).map((a) => ({ value: a, label: a })),
    { value: "__none", label: "담당 미정" },
  ], [me, assignees]);

  const executionFilter = useMemo(
    () => (t: ExecutionTask) =>
      (!hideDone || t.status !== "done") &&
      (!assignee || (assignee === "__none" ? t.assignees.length === 0 : t.assignees.includes(assignee))),
    [hideDone, assignee],
  );

  const events = useMemo(
    () => buildEvents({ project, schedule, layers, today, executionFilter }),
    [project, schedule, layers, today, executionFilter],
  );

  const unscheduled = useMemo(
    () => unscheduledTasks(project.executionTasks, executionFilter),
    [project.executionTasks, executionFilter],
  );

  const rows = useMemo(() => monthGrid(anchor), [anchor]);
  const monthKey = anchor.slice(0, 7);

  const go = (dir: -1 | 1) => {
    setAnchor(fromDay(addMonthsDay(toDay(anchor), dir)));
  };

  const dayEvents = useMemo(
    () => eventsOnDay(events, toDay(anchor)),
    [events, anchor],
  );

  const dropOnDay = (day: DayNumber) => {
    if (!drag) return;
    const task = project.executionTasks.find((t) => t.id === drag);
    if (task) {
      const target = fromDay(day);
      const patch = task.start || task.due ? moveExecutionDates(task, target) : { due: target };
      s().updateExecutionTask(task.id, patch, `캘린더에서 ${formatDate(target, "numeric")}로 이동`);
    }
    setDrag(null);
    setOverDay(null);
  };

  const onEventOpen = (e: CalendarEvent) => {
    if (e.kind === "execution") {
      setPeek(null);
      s().setExecutionFocus(e.refId);
      return;
    }
    s().setExecutionFocus(null);
    setPeek({ id: e.refId, kind: e.kind });
  };

  const dragStart = (id: string) => (e: DragEvent) => {
    e.dataTransfer.setData(DND_TYPE, id);
    e.dataTransfer.effectAllowed = "move";
    setDrag(id);
  };

  const dragEnd = () => {
    setDrag(null);
    setOverDay(null);
  };

  const toggleLayer = (l: CalendarLayer) => {
    setLayers((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <CalendarHeader
          onPrev={() => go(-1)}
          onNext={() => go(1)}
          onToday={() => setAnchor(todayISO())}
          hideDone={hideDone}
          onHideDoneChange={setHideDone}
          assignee={assignee}
          onAssigneeChange={setAssignee}
          assigneesList={assigneesList}
        />
      </div>

      <div className={styles.body}>
      <CalendarSidebar
        anchor={anchor}
        onAnchorChange={setAnchor}
        layers={layers}
        onToggleLayer={toggleLayer}
        dayEvents={dayEvents}
        onEventClick={onEventOpen}
        unscheduled={unscheduled}
        onTaskClick={(taskId) => s().setExecutionFocus(taskId)}
        dragTaskId={drag}
        canDrag={canPlace}
        onDragStart={dragStart}
        onDragEnd={dragEnd}
      />

      <main className={styles.main}>

        {/* Calendar Grid — weekday header scrolls with day cells */}
        <div className={styles.gridWrap}>
          <div className={styles.gridInner}>
            <div className={styles.weekDaysHeader}>
              {WEEKDAYS.map((w, idx) => (
                <div
                  key={w}
                  className={styles.weekDayCell}
                  style={{
                    color: idx === 0 ? tokens.colorPaletteRedForeground1 : idx === 6 ? tokens.colorBrandForeground1 : undefined,
                  }}
                >
                  {w}
                </div>
              ))}
            </div>
            <div className={styles.gridBody}>
            {rows.map((week, rIdx) => {
              const segs = layoutWeek(events, week[0]!);
              const maxLanes = 4;
              const shownSegs = segs.filter((x) => x.lane < maxLanes);
              const laneCount = Math.min(maxLanes, Math.max(1, ...segs.map((x) => x.lane + 1)));
              const minH = Math.max(115, TOP_OFFSET + laneCount * (LANE_H + LANE_GAP) + 24);

              return (
                <div
                  key={rIdx}
                  className={styles.weekRow}
                  style={{ minHeight: `${minH}px`, flexBasis: `${minH}px` }}
                >
                  <div className={styles.weekGrid}>
                    {week.map((day) => {
                      const iso = fromDay(day);
                      const working = calendar.isWorkingISO(iso);
                      const hidden = eventsOnDay(events, day).filter((e) =>
                        segs.some((x) => x.event.id === e.id && x.lane >= maxLanes),
                      );

                      return (
                        <CalendarDayCell
                          key={day}
                          day={day}
                          iso={iso}
                          today={today}
                          monthKey={monthKey}
                          isWorking={working}
                          hiddenEvents={hidden}
                          isSelected={iso === anchor}
                          onSelect={() => setAnchor(iso)}
                          isDragOver={overDay === day}
                          onDragOver={(e) => {
                            if (!drag) return;
                            e.preventDefault();
                            if (overDay !== day) setOverDay(day);
                          }}
                          onDragLeave={(e) => {
                            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                            if (overDay === day) setOverDay(null);
                          }}
                          onDrop={(e) => {
                            if (!canPlace) return;
                            e.preventDefault();
                            dropOnDay(day);
                          }}
                          onOpenEvent={onEventOpen}
                        />
                      );
                    })}
                  </div>

                  <div className={styles.lanes}>
                    {shownSegs.map((seg) => (
                      <EventSegment
                        key={seg.event.id}
                        seg={seg}
                        laneH={LANE_H}
                        laneGap={LANE_GAP}
                        topOffset={TOP_OFFSET}
                        dragging={drag === seg.event.refId}
                        onDragStart={canPlace && seg.event.kind === "execution" ? dragStart(seg.event.refId) : undefined}
                        onDragEnd={dragEnd}
                        onOpen={onEventOpen}
                        today={today}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* Minimalist Legend at Bottom */}
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.colorDot} style={{ backgroundColor: LAYER_COLORS.execution }} /> Task
          </span>
          <span className={styles.legendItem}>
            <span className={styles.colorDot} style={{ backgroundColor: LAYER_COLORS.activity }} /> 계획 작업
          </span>
          <span className={styles.legendItem}>
            <span className={styles.colorDot} style={{ backgroundColor: LAYER_COLORS.wbs }} /> WBS 구간
          </span>
          <span className={styles.legendItem}>
            <Diamond12Filled style={{ color: "#0d9488" }} /> 마일스톤
          </span>
          <span className={styles.legendItem}>
            <Flag16Filled style={{ color: LAYER_COLORS.deadline, fontSize: 13 }} /> 마감 제약
          </span>
          {canPlace && (
            <Caption1 style={{ marginLeft: "auto", color: tokens.colorNeutralForeground4 }}>
              Task는 날짜 셀로 드래그하여 마감일을 변경할 수 있습니다.
            </Caption1>
          )}
        </div>
      </main>
      </div>
      <SchedulePeekDrawer
        taskId={peek?.id ?? null}
        kind={peek?.kind ?? null}
        onClose={() => setPeek(null)}
        onViewTasks={(scheduleTaskId) => {
          setLinkedScheduleId(scheduleTaskId);
          setPeek(null);
        }}
      />
      <LinkedTasksDialog
        open={linkedScheduleId != null}
        scheduleTaskId={linkedScheduleId ?? ""}
        onClose={() => setLinkedScheduleId(null)}
      />
    </div>
  );
}

/** Undated execution-task chip in calendar tray (compatibility export) */
export function CalendarTrayChip({
  title,
  assignee,
}: {
  title?: string;
  assignee?: string;
  dragging?: boolean;
  onClick?: () => void;
  onDragStart?: (e: DragEvent<HTMLElement>) => void;
  onDragEnd?: () => void;
}) {
  return (
    <div>
      {title} {assignee ? `(${assignee})` : ""}
    </div>
  );
}

/** Single event chip sample (compatibility export) */
export function CalendarEventSample({ event }: { event: CalendarEvent; width?: number }) {
  return <div>{event.title}</div>;
}
